import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as customItemActions from '@redux/modules/customItem'

import defaultFormState, { requiredFields } from '@models/customItem'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { CustomItemModel, CustomItemRequestOptions } from '@models/types'

export const generateUrls = (customItem?: Partial<CustomItemModel>, options: { prefixRoute?: string } = {}) => {
  const { id } = customItem || {}

  const { prefixRoute = 'systemManager' } = options

  return {
    editCustomItemDetailsUrl: `#/${prefixRoute}/customItems/${id}/edit/details`,
    editCustomItemUrl: `#/${prefixRoute}/customItems/${id}/edit/`,
    customItemsIndexUrl: `#/${prefixRoute}/customItems`,
  }
}

type CreateCustomItemParams = {
  customItemParams: Partial<CustomItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CustomItemRequestOptions,
}

const createCustomItem = (params: CreateCustomItemParams) => {
  const { dispatch, customItemParams, requestOptions } = params
  const { createCustomItem: createFn } = customItemActions

  return dispatch(createFn(customItemParams, requestOptions))
}

type UpdateCustomItemParams = {
  customItem: CustomItemModel,
  customItemParams: Partial<CustomItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CustomItemRequestOptions,
}

const updateCustomItem = (params: UpdateCustomItemParams) => {
  const {
    dispatch, customItem, customItemParams, requestOptions,
  } = params
  const { updateCustomItem: updateFn } = customItemActions

  const updatedParams = {
    id: customItem.id,
    ...customItemParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCustomItemParams = {
  customItem: DeleteParams<CustomItemModel>,
  dispatch: AppDispatch,
}

const deleteCustomItem = (params: DeleteCustomItemParams) => {
  const { dispatch, customItem } = params
  const { deleteCustomItem: deleteFn } = customItemActions

  return dispatch(deleteFn(customItem))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCustomItemForm(
  customItem: Partial<CustomItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const customItemForm = useForm(
    defaultFormState,
    { entity: customItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [customItem.id, customItem.cache_key],
  )

  return {
    ...customItemForm,
  }
}

export const useRelations = (customItem: Partial<CustomItemModel> = {}) => {
  const { agency_id, custom_item_group_id } = customItem

  const { agencies, customItemGroups } = useSelector(reduxState => reduxState.entities)

  const agency = agency_id && agencies[agency_id] ? agencies[agency_id] : {}
  const customItemGroup = custom_item_group_id && customItemGroups[custom_item_group_id]
    ? customItemGroups[custom_item_group_id]
    : {}

  return {
    agency,
    customItemGroup,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CustomItemRequestOptions,
}

function useCustomItem(initEntity: Partial<CustomItemModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: customItem }: { entity: CustomItemModel} = useLatestEntity(initEntity, 'customItems')
  const { id } = customItem

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  // @ts-expect-error TS2339
  const { relatedEntity } = useRelations(customItem)

  useReduxAction(
    'customItems',
    'loadCustomItem',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.customItems)

  return {
    callbacks: {
      createCustomItem: (customItemParams: Partial<CustomItemModel>, entityOptions?: CustomItemRequestOptions) => (
        createCustomItem({ customItemParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditCustomItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCustomItemModal',
        payload: { customItem },
      }),
      deleteCustomItem: () => deleteCustomItem({ dispatch, customItem }),
      updateCustomItem: (customItemParams: Partial<CustomItemModel>, entityOptions?: CustomItemRequestOptions) => (
        updateCustomItem({
          customItem, customItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    customItem,
    deleting,
    loading: loading && !loadedIds.includes(customItem.id),
    relatedEntity,
    updating,
    urls: generateUrls(customItem),
  }
}

export default useCustomItem
